import type { TableConfig } from './TableConfigModal';

/**
 * Pure seat-layout helpers shared by the Visual Table Planner page and the table settings modal.
 * Keeping one implementation stops the two from drifting apart (they previously disagreed on
 * rectangular tables with seats on one side only).
 */

export const MIN_TABLE_SEATS = 1;
export const MAX_TABLE_SEATS = 100;
const CHAIR_OFFSET = 30;

type Seat = TableConfig['seating'][number];
type SeatCounts = NonNullable<TableConfig['seatCounts']>;
type SideKey = keyof SeatCounts;

export function clampSeatCount(value: number): number {
  if (!Number.isFinite(value)) return MIN_TABLE_SEATS;
  return Math.min(MAX_TABLE_SEATS, Math.max(MIN_TABLE_SEATS, Math.round(value)));
}

/** Spreads `extra` seats round-robin across `sides`, starting with the side holding the fewest. */
function distributeExtra(counts: Record<string, number>, sides: string[], extra: number) {
  for (let i = 0; i < extra; i++) {
    let target = sides[0];
    for (const side of sides) {
      if (counts[side] < counts[target]) target = side;
    }
    counts[target] += 1;
  }
}

/**
 * Positions every seat around the table (seat x/y are relative to the table centre) and resizes the
 * seating array to `seats`. Guest assignments are copied by index; use {@link reflowTable} when the
 * layout changes and guests should follow their side.
 */
export function calculateSeatingPositions(config: TableConfig): TableConfig {
  const { type, width, height, seats, seating, seatPlacement, seatCounts } = config;

  const updatedSeating: Seat[] = [...(seating ?? [])];
  while (updatedSeating.length < seats) {
    updatedSeating.push({
      seatNumber: updatedSeating.length + 1,
      rsvpId: null,
      role: null,
      tag: null,
      x: 0,
      y: 0,
    });
  }
  updatedSeating.splice(seats);

  const place = (index: number, x: number, y: number) => {
    updatedSeating[index] = { ...updatedSeating[index], x, y };
  };

  switch (type) {
    case 'circular':
    case 'oval': {
      const radiusX = width / 2;
      const radiusY = type === 'oval' ? height / 2 : width / 2;
      const angleStep = (2 * Math.PI) / Math.max(1, seats);
      for (let i = 0; i < seats; i++) {
        const angle = angleStep * i - Math.PI / 2;
        place(i, Math.cos(angle) * (radiusX + CHAIR_OFFSET), Math.sin(angle) * (radiusY + CHAIR_OFFSET));
      }
      break;
    }

    case 'rectangular': {
      if (seatPlacement?.noSeats) {
        for (let i = 0; i < updatedSeating.length; i++) place(i, 0, 0);
        break;
      }

      const leftEnd = Math.max(0, seatCounts?.leftEnd ?? (seatPlacement?.leftEnd ? 1 : 0));
      const rightEnd = Math.max(0, seatCounts?.rightEnd ?? (seatPlacement?.rightEnd ? 1 : 0));
      const remainingForSides = Math.max(0, seats - leftEnd - rightEnd);
      const topEnabled = seatPlacement?.topSide ?? seatPlacement?.bothSides ?? true;
      const bottomEnabled = seatPlacement?.bottomSide ?? seatPlacement?.bothSides ?? true;
      const counts: Record<string, number> = {
        top: Math.max(0, seatCounts?.top ?? (topEnabled ? (bottomEnabled ? Math.ceil(remainingForSides / 2) : remainingForSides) : 0)),
        bottom: 0,
      };
      counts.bottom = Math.max(0, seatCounts?.bottom ?? (bottomEnabled ? remainingForSides - counts.top : 0));
      // Never leave seats unplaced (they would sit invisibly at the table centre).
      const placed = leftEnd + rightEnd + counts.top + counts.bottom;
      if (placed < seats) {
        const sides = [topEnabled ? 'top' : null, bottomEnabled ? 'bottom' : null].filter(Boolean) as string[];
        distributeExtra(counts, sides.length > 0 ? sides : ['top', 'bottom'], seats - placed);
      }

      let seatIndex = 0;
      for (let i = 0; i < leftEnd && seatIndex < seats; i++, seatIndex++) {
        place(seatIndex, -width / 2 - CHAIR_OFFSET, (height / (leftEnd + 1)) * (i + 1) - height / 2);
      }
      for (let i = 0; i < rightEnd && seatIndex < seats; i++, seatIndex++) {
        place(seatIndex, width / 2 + CHAIR_OFFSET, (height / (rightEnd + 1)) * (i + 1) - height / 2);
      }
      for (let i = 0; i < counts.top && seatIndex < seats; i++, seatIndex++) {
        place(seatIndex, (width / (counts.top + 1)) * (i + 1) - width / 2, -height / 2 - CHAIR_OFFSET);
      }
      for (let i = 0; i < counts.bottom && seatIndex < seats; i++, seatIndex++) {
        place(seatIndex, (width / (counts.bottom + 1)) * (i + 1) - width / 2, height / 2 + CHAIR_OFFSET);
      }
      break;
    }

    case 'square': {
      if (seatPlacement?.noSeats) {
        for (let i = 0; i < updatedSeating.length; i++) place(i, 0, 0);
        break;
      }

      const quarter = Math.ceil(seats / 4);
      const counts: Record<string, number> = {
        top: Math.max(0, seatCounts?.top ?? (seatPlacement?.topSide ?? true ? quarter : 0)),
        bottom: Math.max(0, seatCounts?.bottom ?? (seatPlacement?.bottomSide ?? true ? quarter : 0)),
        left: Math.max(0, seatCounts?.left ?? (seatPlacement?.leftSide ?? true ? quarter : 0)),
        right: 0,
      };
      counts.right = Math.max(
        0,
        seatCounts?.right ?? (seatPlacement?.rightSide ?? true ? seats - counts.top - counts.bottom - counts.left : 0)
      );
      const placed = counts.top + counts.bottom + counts.left + counts.right;
      if (placed < seats) {
        const sides = (['top', 'right', 'bottom', 'left'] as const).filter((side) => {
          const flag = seatPlacement?.[`${side}Side` as const];
          return flag ?? true;
        });
        distributeExtra(counts, sides.length > 0 ? [...sides] : ['top', 'right', 'bottom', 'left'], seats - placed);
      }

      let seatIndex = 0;
      for (let i = 0; i < counts.top && seatIndex < seats; i++, seatIndex++) {
        place(seatIndex, (width / (counts.top + 1)) * (i + 1) - width / 2, -height / 2 - CHAIR_OFFSET);
      }
      for (let i = 0; i < counts.right && seatIndex < seats; i++, seatIndex++) {
        place(seatIndex, width / 2 + CHAIR_OFFSET, (height / (counts.right + 1)) * (i + 1) - height / 2);
      }
      for (let i = 0; i < counts.bottom && seatIndex < seats; i++, seatIndex++) {
        place(seatIndex, width / 2 - (width / (counts.bottom + 1)) * (i + 1), height / 2 + CHAIR_OFFSET);
      }
      for (let i = 0; i < counts.left && seatIndex < seats; i++, seatIndex++) {
        place(seatIndex, -width / 2 - CHAIR_OFFSET, height / 2 - (height / (counts.left + 1)) * (i + 1));
      }
      break;
    }
  }

  return { ...config, seating: updatedSeating };
}

/** Which edge of a rectangular/square table a seat sits on (seat x/y are relative to the centre). */
export function getSeatSide(seat: { x: number; y: number }, table: Pick<TableConfig, 'width'>): string {
  const { width } = table;
  if (seat.x <= -width / 2 - CHAIR_OFFSET + 10) return 'leftEnd';
  if (seat.x >= width / 2 + CHAIR_OFFSET - 10) return 'rightEnd';
  if (seat.y < 0) return 'top';
  if (seat.y > 0) return 'bottom';
  if (seat.x < 0) return 'left';
  if (seat.x > 0) return 'right';
  return 'unknown';
}

function isRound(table: Pick<TableConfig, 'type'>) {
  return table.type === 'circular' || table.type === 'oval';
}

/** Orders a side's seats along the edge so the n-th guest on a side stays n-th after a reflow. */
function sideOrder(seat: { x: number; y: number }, side: string) {
  return side === 'top' || side === 'bottom' ? seat.x : seat.y;
}

/**
 * Moves the guests of `oldTable` onto the re-laid-out `nextTable`:
 * - round tables keep each guest's seat index (seats run clockwise from the top);
 * - rectangular/square tables keep guests on the same side, in the same order along it.
 * Guests that no longer fit where they were are moved into the first empty seat; only when the
 * table has fewer seats than guests are any left out, and their ids are returned.
 */
export function reseatGuests(
  oldTable: TableConfig,
  nextTable: TableConfig
): { table: TableConfig; unseatedGuestIds: string[] } {
  const seating: Seat[] = nextTable.seating.map((s) => ({ ...s, rsvpId: null, role: null }));
  const placed = new Set<string>();
  const oldGuests = oldTable.seating.filter((s) => s.rsvpId);

  if (isRound(oldTable) || isRound(nextTable) || oldTable.type !== nextTable.type) {
    oldTable.seating.forEach((seat, index) => {
      if (seat.rsvpId && index < seating.length) {
        seating[index] = { ...seating[index], rsvpId: seat.rsvpId, role: seat.role };
        placed.add(seat.rsvpId);
      }
    });
  } else {
    const oldBySide = new Map<string, Seat[]>();
    for (const seat of oldTable.seating) {
      const side = getSeatSide(seat, oldTable);
      oldBySide.set(side, [...(oldBySide.get(side) ?? []), seat]);
    }
    const newBySide = new Map<string, number[]>();
    seating.forEach((seat, index) => {
      const side = getSeatSide(seat, nextTable);
      newBySide.set(side, [...(newBySide.get(side) ?? []), index]);
    });
    for (const [side, oldSeats] of oldBySide) {
      const guests = [...oldSeats]
        .sort((a, b) => sideOrder(a, side) - sideOrder(b, side))
        .filter((s) => s.rsvpId);
      const targets = [...(newBySide.get(side) ?? [])].sort(
        (a, b) => sideOrder(seating[a], side) - sideOrder(seating[b], side)
      );
      // Keep each guest at the same relative slot along the side where possible.
      const oldSorted = [...oldSeats].sort((a, b) => sideOrder(a, side) - sideOrder(b, side));
      for (const guest of guests) {
        const slot = Math.min(oldSorted.indexOf(guest), targets.length - 1);
        const free = [...targets.slice(slot), ...targets.slice(0, slot).reverse()].find(
          (idx) => !seating[idx].rsvpId
        );
        if (free !== undefined && guest.rsvpId) {
          seating[free] = { ...seating[free], rsvpId: guest.rsvpId, role: guest.role };
          placed.add(guest.rsvpId);
        }
      }
    }
  }

  const unseatedGuestIds: string[] = [];
  for (const guest of oldGuests) {
    if (!guest.rsvpId || placed.has(guest.rsvpId)) continue;
    const free = seating.findIndex((s) => !s.rsvpId);
    if (free >= 0) {
      seating[free] = { ...seating[free], rsvpId: guest.rsvpId, role: guest.role };
      placed.add(guest.rsvpId);
    } else {
      unseatedGuestIds.push(guest.rsvpId);
    }
  }

  return { table: { ...nextTable, seating }, unseatedGuestIds };
}

/** Applies a layout change (type, size, seat counts…) and keeps guests seated where possible. */
export function reflowTable(
  table: TableConfig,
  patch: Partial<TableConfig>
): { table: TableConfig; unseatedGuestIds: string[] } {
  const next = calculateSeatingPositions({ ...table, ...patch });
  return reseatGuests(table, next);
}

function sidesFor(type: TableConfig['type']): SideKey[] {
  return type === 'square' ? ['top', 'right', 'bottom', 'left'] : ['top', 'bottom'];
}

/**
 * Changes a table's total seat count. Custom per-side counts are kept and the difference is added
 * to the emptiest side (or taken from the fullest), so seats never end up unplaced.
 */
export function resizeTableSeats(
  table: TableConfig,
  requestedSeats: number
): { table: TableConfig; unseatedGuestIds: string[] } {
  const seats = clampSeatCount(requestedSeats);
  let seatCounts = table.seatCounts;

  if (seatCounts && !isRound(table)) {
    const counts: Record<SideKey, number> = {
      top: seatCounts.top ?? 0,
      bottom: seatCounts.bottom ?? 0,
      left: seatCounts.left ?? 0,
      right: seatCounts.right ?? 0,
      leftEnd: seatCounts.leftEnd ?? 0,
      rightEnd: seatCounts.rightEnd ?? 0,
    };
    const sides = sidesFor(table.type);
    const ends: SideKey[] = table.type === 'rectangular' ? ['leftEnd', 'rightEnd'] : [];
    const total = () => [...sides, ...ends].reduce((sum, side) => sum + counts[side], 0);
    while (total() < seats) {
      const target = sides.reduce((min, side) => (counts[side] < counts[min] ? side : min), sides[0]);
      counts[target] += 1;
    }
    while (total() > seats) {
      const withSeats = sides.filter((side) => counts[side] > 0);
      const pool = withSeats.length > 0 ? withSeats : ends.filter((side) => counts[side] > 0);
      if (pool.length === 0) break;
      const target = pool.reduce((max, side) => (counts[side] > counts[max] ? side : max), pool[0]);
      counts[target] -= 1;
    }
    seatCounts = Object.fromEntries(
      [...sides, ...ends].map((side) => [side, counts[side]])
    ) as SeatCounts;
  }

  return reflowTable(table, { seats, seatCounts });
}

/** Clears the given guests from every seat. `changed` is false when none of them was seated. */
export function removeGuestsFromTables(
  tables: TableConfig[],
  guestIds: ReadonlySet<string>
): { tables: TableConfig[]; changed: boolean } {
  let changed = false;
  const next = tables.map((table) => {
    if (!table.seating?.some((s) => s.rsvpId && guestIds.has(s.rsvpId))) return table;
    changed = true;
    return {
      ...table,
      seating: table.seating.map((s) =>
        s.rsvpId && guestIds.has(s.rsvpId) ? { ...s, rsvpId: null, role: null } : s
      ),
    };
  });
  return { tables: changed ? next : tables, changed };
}

/**
 * Three-way merge of a floor plan's tables, per table id. `base` is what this client last saved or
 * received, `local` holds its unsaved edits and `remote` is the newer server copy. Tables this client
 * changed or added keep the local version, tables it deleted stay deleted, and everything else
 * follows the server — so two people editing different tables don't overwrite each other.
 */
export function mergeTables(
  base: TableConfig[],
  local: TableConfig[],
  remote: TableConfig[]
): TableConfig[] {
  const key = (t: TableConfig | undefined) => (t ? JSON.stringify(t) : '');
  const baseById = new Map(base.map((t) => [t.id, t]));
  const localById = new Map(local.map((t) => [t.id, t]));
  const remoteIds = new Set(remote.map((t) => t.id));
  const merged: TableConfig[] = [];

  for (const remoteTable of remote) {
    const baseTable = baseById.get(remoteTable.id);
    const localTable = localById.get(remoteTable.id);
    if (!localTable) {
      // Deleted locally: keep it deleted unless it was never in our base (added remotely).
      if (!baseTable) merged.push(remoteTable);
      continue;
    }
    const changedLocally = !baseTable || key(localTable) !== key(baseTable);
    merged.push(changedLocally ? localTable : remoteTable);
  }
  for (const localTable of local) {
    if (remoteIds.has(localTable.id)) continue;
    const baseTable = baseById.get(localTable.id);
    // Added locally, or edited locally after someone else deleted it: keep ours.
    if (!baseTable || key(localTable) !== key(baseTable)) merged.push(localTable);
  }
  return merged;
}

/**
 * Plan-space bounding box of every table body and seat, honouring each table's rotation.
 * Table x/y is the table centre; seat x/y are relative to it.
 */
export function getTablesBounds(
  tables: TableConfig[]
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const t of tables) {
    const rad = ((t.rotation || 0) * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const points = [
      { x: -t.width / 2, y: -t.height / 2 },
      { x: t.width / 2, y: -t.height / 2 },
      { x: -t.width / 2, y: t.height / 2 },
      { x: t.width / 2, y: t.height / 2 },
      ...(t.seating ?? []).map((s) => ({ x: s.x, y: s.y })),
    ];
    for (const p of points) {
      const x = t.x + p.x * cos - p.y * sin;
      const y = t.y + p.x * sin + p.y * cos;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }
  return Number.isFinite(minX) ? { minX, minY, maxX, maxY } : null;
}
